import { useState } from 'react';
import type { MsrUebersicht } from './typen';
import { ART, GEWERK, HERKUNFT, z, zProzent } from './gemeinsam';
import { zeitpunkt } from './kopf';
import {
  ContentSection, DataTable, EmptyState, KpiCard, KpiReihe, PropertyGrid,
  StatusBadge, type Spalte,
} from '../../muster';

const ART_SCHLUESSEL: (keyof typeof ART)[] = ['BE', 'BA', 'AE', 'AA', 'ZE', 'KOM'];

/**
 * Die ASP-Arten für die Kennzahlenkarte — kurz. `ASP_ART` schreibt
 * „Lage (Quadrant.Geschoss)" aus; in der schmalen Spalte einer Karte
 * sprengt das die Breite, ohne mehr zu sagen. Die lange Form steht
 * weiterhin in der ASP-Tabelle.
 */
const ASP_ART_KURZ: Record<string, string> = {
  lage: 'Lage', gewerk: 'Gewerk', anlage: 'Anlage', entrauchung: 'Entrauchung',
};

type Einstellung = MsrUebersicht['einstellungen'][number];
type Gegenprobe = NonNullable<MsrUebersicht['letzterLauf']>['gegenproben'][number];

/**
 * Reiter „Übersicht" — Kennzahlenkarten, letzter Ableitungslauf mit
 * Gegenproben, Festlegungen. Der Knopf „Ableitung ausführen" liegt im Kopf
 * der Seite (Msr.tsx) und nicht hier — er wirkt auf die ganze Seite, nicht
 * nur auf diesen Reiter.
 *
 * Umzug U6c: Kennzahlen auf `KpiCard`, Tabellen auf `DataTable`, Statusfarben
 * auf `StatusBadge`. Die Karte „Datenpunkte" behält ihre eigene Balkengrafik —
 * sie ist eine Verteilung über sechs Datenpunktarten und keine Kennzahl.
 */
export function Uebersicht({ u }: { u: MsrUebersicht }) {
  const [alleEinstellungen, setAlleEinstellungen] = useState(false);

  const geschaetztAnteil = u.datenpunkte.summe > 0
    ? (100 * (u.datenpunkte.jeHerkunft.geschaetzt ?? 0)) / u.datenpunkte.summe
    : 0;

  // Fehlt die Einstellung, ist das Projekt noch in Bearbeitung — der übliche
  // Wert (0 = nicht eingefroren), keine Rückfrage nötig (Memo „Übliche Werte
  // statt Rückfrage").
  const aksEingefroren = u.einstellungen.find((e) => e.schluessel === 'aks_eingefroren');
  const eingefroren = aksEingefroren
    ? (aksEingefroren.zahlwert ?? Number(aksEingefroren.wert)) !== 0
    : false;

  const aufzaehlung = (eintraege: [string, number][], namen: Record<string, string>) =>
    eintraege.map(([k, n]) => `${namen[k] ?? k} ${z(n)}`).join(' · ');

  const gegenprobenSpalten: Spalte<Gegenprobe>[] = [
    {
      schluessel: 'ergebnis', titel: 'Ergebnis', art: 'status', breite: '130px',
      wert: (g) => g.ergebnis,
      inhalt: (g) => (
        <StatusBadge
          klein
          zustand={g.ergebnis === 'ok' ? 'gut' : g.ergebnis === 'warnung' ? 'warnung' : 'fehler'}
          text={g.ergebnis === 'ok' ? 'in Ordnung' : g.ergebnis === 'warnung' ? 'Prüfen' : 'Fehler'}
        />
      ),
    },
    { schluessel: 'aussage', titel: 'Gegenprobe', wert: (g) => g.aussage, immerSichtbar: true },
    { schluessel: 'zahl', titel: 'Zahl', art: 'zahl', wert: (g) => g.zahl ?? null },
  ];

  const einstellungSpalten: Spalte<Einstellung>[] = [
    { schluessel: 'schluessel', titel: 'Schlüssel', wert: (e) => e.schluessel, immerSichtbar: true },
    { schluessel: 'wert', titel: 'Wert', wert: (e) => e.zahlwert ?? e.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (e) => e.einheit ?? null },
    { schluessel: 'begruendung', titel: 'Begründung', wert: (e) => e.begruendung ?? null },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (e) => HERKUNFT[e.herkunft] },
  ];

  const einstellungen = u.einstellungen
    .filter((e) => alleEinstellungen || e.herkunft === 'festlegung');

  return (
    <>
      <KpiReihe>
        <KpiCard
          titel="ASPs" wert={u.asps.gesamt}
          vergleich={aufzaehlung(Object.entries(u.asps.jeArt) as [string, number][], ASP_ART_KURZ)}
          zustand={u.asps.ueberKapazitaet > 0 ? 'fehler'
            : u.asps.ohneEinspeisung > 0 ? 'warnung' : undefined}
          zustandText={u.asps.ueberKapazitaet > 0
            ? `${z(u.asps.ueberKapazitaet)} über Kapazität`
            : u.asps.ohneEinspeisung > 0
              ? `${z(u.asps.ohneEinspeisung)} ohne Einspeisung` : undefined}
        />

        <KpiCard
          titel="Anlagen" wert={u.anlagen.gesamt}
          vergleich={aufzaehlung(Object.entries(u.anlagen.jeGewerk) as [string, number][], GEWERK)}
        />

        {/* Zähler je Quelle (Gewerk): zeigt auch die Feldgeräte-Quellen der
            Fragen 71/69 (AVS, Zirkulation, sv-Verteilung) einzeln, statt sie
            in der Gesamtzahl zu verstecken. */}
        <KpiCard
          titel="Feldgeräte" wert={u.feldgeraete.gesamt}
          vergleich={aufzaehlung(
            Object.entries(u.feldgeraete.jeGewerk) as [string, number][], GEWERK,
          )}
          fussnote={`${z(u.feldgeraete.mitFabrikat)} mit Fabrikat`
            + (u.feldgeraete.ohneAsp > 0 ? `, ${z(u.feldgeraete.ohneAsp)} ohne ASP` : '')}
          zustand={u.feldgeraete.ohneAsp > 0 ? 'warnung' : undefined}
          zustandText={u.feldgeraete.ohneAsp > 0
            ? `${z(u.feldgeraete.ohneAsp)} ohne ASP` : undefined}
        />

        <KpiCard
          titel="GA-Funktionen" wert={u.funktionen.gesamt}
          vergleich={`${z(u.funktionen.anlagenMitBeschreibung)} Anlagen mit Beschreibung, `
            + `davon ${z(u.funktionen.anlagenMitKi)} KI-erzeugt`}
        />

        <KpiCard
          titel="Raumautomation" wert={u.raumautomation.raeume} einheit="Räume"
          vergleich={`${z(u.raumautomation.datenpunkteKlemme)} Klemme · `
            + `${z(u.raumautomation.datenpunkteBus)} Bus`}
        />
      </KpiReihe>

      <ContentSection
        titel="Datenpunkte"
        beschreibung="Datenpunktlisten nach VDI 3814 Blatt 4.3 im Aufbau der Vorgabe Teuber + Viel."
        meta={`${z(u.datenpunkte.summe)} gesamt`}
      >
        <div style={{ maxWidth: 420 }}>
          {ART_SCHLUESSEL.map((a) => {
            const wert = u.datenpunkte[a.toLowerCase() as 'be'] as number;
            const breite = u.datenpunkte.summe > 0
              ? Math.max(2, (100 * wert) / u.datenpunkte.summe) : 0;
            return (
              <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                     fontSize: 12, marginBottom: 3 }}>
                <span style={{ width: 34 }} title={ART[a]}>{a}</span>
                <span style={{ flex: 1, background: 'var(--flaeche-gedaempft)',
                                borderRadius: 2, height: 7 }}>
                  <span style={{ display: 'block', width: `${breite}%`, height: 7,
                                  borderRadius: 2, background: 'var(--akzent)' }} />
                </span>
                <span style={{ width: 52, textAlign: 'right' }}>{z(wert)}</span>
              </div>
            );
          })}
        </div>

        <PropertyGrid
          spalten={3}
          eintraege={[
            { beschriftung: 'An der Klemme', wert: u.datenpunkte.klemme },
            { beschriftung: 'Über Bus', wert: u.datenpunkte.bus },
            {
              beschriftung: 'AKS vergeben',
              wert: `${z(u.datenpunkte.mitAks)} / ${z(u.datenpunkte.summe)}`,
              hinweis: eingefroren ? undefined
                : 'Solange das Projekt in Bearbeitung ist, werden AKS und Klemmen je Lauf '
                  + 'nach Vorlage neu vergeben (Einstellung aks_eingefroren=0).',
            },
          ]}
        />

        {geschaetztAnteil > 0 && (
          <p style={{ marginBottom: 0 }}>
            <StatusBadge
              klein zustand="warnung"
              text={`${zProzent(geschaetztAnteil)} % geschätzt`}
            />{' '}
            <span className="schwach">
              ({z(u.datenpunkte.jeHerkunft.geschaetzt ?? 0)} Datenpunkte ersetzen einen
              fehlenden Wert durch einen Schätzwert.)
            </span>
          </p>
        )}
      </ContentSection>

      <ContentSection
        titel="Letzter Ableitungslauf"
        meta={u.letzterLauf
          ? zeitpunkt(u.letzterLauf.gestartetAm)
            + (u.letzterLauf.beendetAm ? ` – ${zeitpunkt(u.letzterLauf.beendetAm)}` : '')
            + (u.letzterLauf.ausgefuehrtVon ? ` · ${u.letzterLauf.ausgefuehrtVon}` : '')
          : undefined}
      >
        {!u.letzterLauf ? (
          <EmptyState
            titel="Noch kein Ableitungslauf gelaufen."
            erklaerung={'Datenpunkte, ASPs und AKS-Kennungen entstehen aus der Ableitung über '
              + 'die bereits aufgenommenen Gewerke.'}
            aktion={<span className="schwach">Oben im Seitenkopf: „Ableitung ausführen".</span>}
          />
        ) : (
          <>
            <PropertyGrid
              spalten={3}
              eintraege={Object.entries(u.letzterLauf.zahlen)
                .map(([k, v]) => ({ beschriftung: k, wert: v as number }))}
            />

            <DataTable
              name="Gegenproben"
              spalten={gegenprobenSpalten}
              zeilen={u.letzterLauf.gegenproben}
              schluessel={(g) => g.schluessel}
              dicht
              suche={false}
              spaltenwahl={false}
              leer={<EmptyState klein titel="Der Lauf hat keine Gegenprobe hinterlegt." />}
            />

            {u.letzterLauf.hinweise.length > 0 && (
              <ul style={{ marginTop: 10 }}>
                {u.letzterLauf.hinweise.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            )}
          </>
        )}
      </ContentSection>

      <ContentSection
        titel="Festlegungen"
        beschreibung={alleEinstellungen
          ? 'Alle Einstellungen des Projekts.'
          : 'Nur die ausdrücklichen Festlegungen des Fachplaners.'}
        aktionen={(
          <button type="button" className="m-knopf"
            onClick={() => setAlleEinstellungen((a) => !a)}>
            {alleEinstellungen ? 'nur Festlegungen' : 'alle Einstellungen'}
          </button>
        )}
      >
        <DataTable
          name="Einstellungen"
          spalten={einstellungSpalten}
          zeilen={einstellungen}
          schluessel={(e) => e.schluessel}
          dicht
          ausfuhr
          ausfuhrDateiname="msr-einstellungen"
          leer={(
            <EmptyState
              titel={alleEinstellungen
                ? 'Für dieses Projekt ist keine Einstellung hinterlegt.'
                : 'Es gibt noch keine ausdrückliche Festlegung.'}
              erklaerung={alleEinstellungen
                ? 'Einstellungen entstehen mit dem ersten Ableitungslauf.'
                : 'Ohne Festlegung gelten die Vorlagenwerte der Spezifikation.'}
              aktion={!alleEinstellungen ? (
                <button type="button" className="m-knopf"
                  onClick={() => setAlleEinstellungen(true)}>
                  alle Einstellungen zeigen
                </button>
              ) : undefined}
            />
          )}
        />
      </ContentSection>
    </>
  );
}
